(() => {
  const valueMap = {
    recipient: {
      mum: 'mum',
      dad: 'dad',
      her: 'her',
      him: 'him',
      couple: 'couple',
      child: 'child',
      pet: 'pet',
      teacher: 'teacher',
      in_memory: 'in memory',
    },
    occasion: {
      birthday: 'birthday',
      wedding: 'wedding',
      anniversary: 'anniversary',
      new_baby: 'new baby',
      memorial: 'memorial',
      graduation: 'graduation',
      housewarming: 'housewarming',
      thank_you: 'thank you',
      valentines: "valentine's day",
      mothers_day: "mother's day",
      fathers_day: "father's day",
      easter: 'easter',
      halloween: 'halloween',
      christmas: 'christmas',
    },
    gift_type: {
      mug: 'mugs',
      drinkware: 'drinkware',
      frame: 'frames',
      candle: 'candles',
      jewellery: 'jewellery',
      keepsake: 'keepsakes',
      light: 'lights',
      home_decor: 'home decor',
      trinket_box: 'trinket boxes',
      plush: 'plush',
      card: 'cards',
      stationery: 'stationery',
      bag_textile: 'bags & textiles',
    },
  };

  const routeDimension = {
    recipient: 'recipient',
    occasion: 'occasion',
    gift_type: 'giftType',
  };

  let routeIndexPromise;

  const routeIndexIsComplete = (payload) => {
    const available = Number(payload?.metadata?.availablePersonalisedProducts);
    const indexed = Number(payload?.metadata?.indexedProducts);
    const coverage = Number(payload?.metadata?.strictCoverage);

    return (
      Number.isFinite(available) &&
      available > 0 &&
      Number.isFinite(indexed) &&
      indexed >= available &&
      Number.isFinite(coverage) &&
      coverage >= 0.999999
    );
  };

  const activeSelections = (root) => {
    const selections = {};
    root.querySelectorAll('[data-hf-taxonomy-option].is-active').forEach((option) => {
      const dimension = option.dataset.hfTaxonomyDimension;
      const key = option.dataset.hfTaxonomyKey;
      if (!selections[dimension]) selections[dimension] = valueMap[dimension]?.[key] || '';
    });
    return selections;
  };

  const decodedRoutes = (payload) => {
    if (!Array.isArray(payload?.routes) || !payload?.dimensions) return [];
    return payload.routes.map((row) => ({
      recipient: payload.dimensions.recipient?.[row[0]] || '',
      occasion: payload.dimensions.occasion?.[row[1]] || '',
      giftType: payload.dimensions.giftType?.[row[2]] || '',
      count: Number(row[4]) || 0,
    }));
  };

  const productCount = (routes, selections, candidateDimension, candidateValue) => {
    const target = { ...selections, [candidateDimension]: candidateValue };
    return routes.reduce((count, route) => {
      const matches = Object.entries(target).every(([dimension, value]) => {
        if (!value) return true;
        return route[routeDimension[dimension]] === value;
      });
      return matches ? count + route.count : count;
    }, 0);
  };

  const loadRouteIndex = (url) => {
    if (!routeIndexPromise) {
      routeIndexPromise = fetch(url, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Route index request failed: ${response.status}`);
          return response.json();
        })
        .catch(() => null);
    }
    return routeIndexPromise;
  };

  const applyCompatibility = async (root) => {
    if (!root || root.dataset.hfTaxonomyCompatibilityReady === 'true') return;
    root.setAttribute('aria-busy', 'true');
    const payload = await loadRouteIndex(root.dataset.hfRouteIndexUrl);
    if (!payload) {
      root.removeAttribute('aria-busy');
      return;
    }

    // Compatibility hiding must be fail-open. An incomplete index can otherwise
    // suppress a valid route that Shopify itself can return from the collection.
    if (!routeIndexIsComplete(payload)) {
      root.dataset.hfTaxonomyCompatibilityReady = 'fallback';
      root.removeAttribute('aria-busy');
      return;
    }

    const routes = decodedRoutes(payload);
    if (!routes.length) {
      root.removeAttribute('aria-busy');
      return;
    }
    const selections = activeSelections(root);
    const minimumOccasionProducts = Math.max(1, Number(payload.minimums?.occasion) || 3);
    const focusedElement = document.activeElement;

    root.querySelectorAll('[data-hf-taxonomy-option]').forEach((option) => {
      const dimension = option.dataset.hfTaxonomyDimension;
      const key = option.dataset.hfTaxonomyKey;
      const candidateValue = valueMap[dimension]?.[key];
      const item = option.closest('li');
      if (!item || option.classList.contains('is-active') || !candidateValue) return;

      const otherSelections = { ...selections };
      delete otherSelections[dimension];
      if (!Object.values(otherSelections).some(Boolean)) return;

      const count = productCount(routes, otherSelections, dimension, candidateValue);
      const minimum = dimension === 'occasion' ? minimumOccasionProducts : 1;
      const containsFocus = focusedElement instanceof Element && item.contains(focusedElement);
      item.hidden = count < minimum && !containsFocus;
    });

    root.querySelectorAll('.hf-tag-facet').forEach((facet) => {
      const visibleItems = [...facet.querySelectorAll('.hf-tag-facet__item')].some((item) => !item.hidden);
      const containsFocus = focusedElement instanceof Element && facet.contains(focusedElement);
      facet.hidden = !visibleItems && !containsFocus;
    });
    root.dataset.hfTaxonomyCompatibilityReady = 'true';
    root.removeAttribute('aria-busy');
  };

  const initialise = (scope = document) => {
    scope.querySelectorAll?.('[data-hf-taxonomy-facets]').forEach((root) => applyCompatibility(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches('[data-hf-taxonomy-facets]')) applyCompatibility(node);
        initialise(node);
      });
    });
  });
  const mutationRoot = document.querySelector('main') || document.body;
  if (mutationRoot) observer.observe(mutationRoot, { childList: true, subtree: true });
})();
